import {
  IGanttAffectedPoint,
  IGanttAffectedPointWindow,
  IGanttChartSerieEnvironmentalVariable,
} from "../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

// `affected_vertices` is the key forecast-risk-finder actually writes for a
// multipoint insight (insight_multipoint.py -> InsightAreaToGantt), and it
// survives untouched through the API, which spreads the Mongo document's `data`
// entries. The rest are defensive aliases.
const LIST_KEYS = [
  "affected_vertices",
  "affectedVertices",
  "affected_points",
  "affectedPoints",
  "impacted_points",
  "impactedPoints",
  "points",
];
const NAME_KEYS = [
  "name",
  "point_name",
  "pointName",
  "station_name",
  "stationName",
  "label",
];
const INDEX_KEYS = ["index", "vertex_index", "vertexIndex", "position"];
const LAT_KEYS = ["lat", "latitude", "point_lat", "station_lat"];
const LNG_KEYS = [
  "lng",
  "lon",
  "long",
  "longitude",
  "point_lon",
  "station_lon",
];
const VARIABLE_LIST_KEYS = [
  "environmentalVariables",
  "environmental_variables",
  "variables",
];
const VARIABLE_NAME_KEYS = [
  "environmentalVariable",
  "environmental_variable",
  "variable",
  "name",
];
const WINDOW_LIST_KEYS = [
  "windows",
  "time_windows",
  "timeWindows",
  "periods",
  "intervals",
];
// `x`/`x2` are what Highcharts calls the bounds of a Gantt point, which is the
// row these vertices hang off.
const WINDOW_START_KEYS = ["start", "start_ms", "startMs", "from", "x"];
const WINDOW_END_KEYS = ["end", "end_ms", "endMs", "to", "x2"];
const MIN_KEYS = ["min", "min_value", "minValue"];
const MAX_KEYS = ["max", "max_value", "maxValue"];
const UNIT_KEYS = ["units", "unit"];

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// MobX 6 observable arrays are Proxies over a real array, so Array.isArray holds
// today — Array.from keeps the copy plain regardless.
const readList = (
  source: UnknownRecord,
  keys: string[]
): unknown[] | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (Array.isArray(value)) return Array.from(value);
  }
  return undefined;
};

const readString = (
  source: UnknownRecord,
  keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return undefined;
};

const readNumber = (
  source: UnknownRecord,
  keys: string[]
): number | undefined => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
};

function normalizeVariables(
  raw: unknown[] | undefined
): IGanttChartSerieEnvironmentalVariable[] {
  if (!raw) return [];

  return raw.reduce<IGanttChartSerieEnvironmentalVariable[]>((acc, entry) => {
    if (!isRecord(entry)) return acc;

    const environmentalVariable = readString(entry, VARIABLE_NAME_KEYS);
    if (!environmentalVariable) return acc;

    acc.push({
      environmentalVariable,
      min: readNumber(entry, MIN_KEYS),
      max: readNumber(entry, MAX_KEYS),
      units: readString(entry, UNIT_KEYS) ?? null,
    });
    return acc;
  }, []);
}

function normalizeWindows(
  raw: unknown[] | undefined
): IGanttAffectedPointWindow[] {
  if (!raw) return [];

  return raw
    .reduce<IGanttAffectedPointWindow[]>((acc, entry) => {
      // Degenerate shape: a [start, end] tuple, as with the coordinates below.
      const window = Array.isArray(entry)
        ? { start: entry[0], end: entry[1] }
        : entry;
      if (!isRecord(window)) return acc;

      const start = readNumber(window, WINDOW_START_KEYS);
      const end = readNumber(window, WINDOW_END_KEYS);
      // The backend's end is exclusive, so a window is never zero-width; one
      // that is has lost a bound somewhere and would render as a blank range.
      if (start === undefined || end === undefined || end <= start) return acc;

      acc.push({ start, end });
      return acc;
    }, [])
    .sort((a, b) => a.start - b.start);
}

/**
 * Trims each window to `[start, end]`, dropping the ones that fall outside it.
 *
 * A vertex window is written inside its own event block, but the API strips a
 * gantt row to the requested range without touching the vertices hanging off it
 * (`getGanttChartDataStrip` in the legacy API clamps `start`/`end` only), so an
 * event already under way reports vertices reaching past the row that contains
 * them. The row is the aggregate of its vertices, so a vertex can only ever be
 * equal to it or narrower.
 *
 * Bounds that are not finite numbers leave the list untouched: that is the
 * caller having nothing to clamp against, not an empty range.
 */
export function clampWindowsToRange(
  windows: IGanttAffectedPointWindow[],
  start?: number,
  end?: number
): IGanttAffectedPointWindow[] {
  const hasStart = typeof start === "number" && Number.isFinite(start);
  const hasEnd = typeof end === "number" && Number.isFinite(end);
  if (!hasStart && !hasEnd) return windows;

  return windows.reduce<IGanttAffectedPointWindow[]>((acc, window) => {
    const clamped = {
      start: hasStart ? Math.max(window.start, start as number) : window.start,
      end: hasEnd ? Math.min(window.end, end as number) : window.end,
    };
    // Same test normalizeWindows applies: a window that has collapsed no longer
    // describes a period, it just renders as a blank range.
    if (clamped.end <= clamped.start) return acc;

    acc.push(clamped);
    return acc;
  }, []);
}

/**
 * Reads the vertices of a multipoint target that actually triggered the event
 * out of a Gantt row. The Gantt payload is never validated (the axios response
 * is cast straight into IGanttChartData), so the field name is matched against
 * an alias list and every unreadable entry is dropped instead of throwing.
 *
 * Returns [] whenever nothing usable is present — that is what keeps the
 * tooltip byte-identical for area and single-point targets.
 */
export function normalizeAffectedPoints(
  source: unknown
): IGanttAffectedPoint[] {
  if (!isRecord(source)) return [];

  const raw = readList(source, LIST_KEYS);
  if (!raw) return [];

  return raw.reduce<IGanttAffectedPoint[]>((acc, entry) => {
    // Degenerate shape: a bare label list, ["Berço 1", "Berço 2"].
    if (typeof entry === "string" && entry.trim() !== "") {
      acc.push({ name: entry.trim(), windows: [], environmentalVariables: [] });
      return acc;
    }

    // Degenerate shape: a coordinate tuple, [[-12.9, -38.5], ...].
    if (Array.isArray(entry) && entry.length >= 2) {
      const [lat, lng] = entry;
      if (
        typeof lat === "number" &&
        Number.isFinite(lat) &&
        typeof lng === "number" &&
        Number.isFinite(lng)
      ) {
        acc.push({ lat, lng, windows: [], environmentalVariables: [] });
      }
      return acc;
    }

    if (!isRecord(entry)) return acc;

    acc.push({
      name: readString(entry, NAME_KEYS),
      index: readNumber(entry, INDEX_KEYS),
      lat: readNumber(entry, LAT_KEYS),
      lng: readNumber(entry, LNG_KEYS),
      windows: normalizeWindows(readList(entry, WINDOW_LIST_KEYS)),
      environmentalVariables: normalizeVariables(
        readList(entry, VARIABLE_LIST_KEYS)
      ),
    });
    return acc;
  }, []);
}

/**
 * Labels a vertex by its coordinates when the payload carries no name, using
 * the same 4-decimal convention already applied to a user-drawn point in
 * src/util/station/createCustomPoint.ts.
 */
export function formatCoordinatesLabel(
  lat?: number,
  lng?: number
): string | undefined {
  if (lat === undefined || lng === undefined) return undefined;
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}
